import React from "react";
import { useNavigate } from "react-router-dom";

import SupportEscalationScreen from "@/b2b/components/SupportEscalationScreen";
import PrimaryButton from "@/b2b/components/PrimaryButton.jsx";

/**
 * Shown when a tenant's bank check did not succeed.
 *
 * ⚠️ Despite the old copy ("We couldn't find your salary account"), this is the CATCH-ALL for every
 * non-success banking outcome, not just a missing salary: both socket handlers route
 * `status !== "success"` straight here. A tenant whose bank connection timed out was being told we
 * could not find their salary, which is simply not what happened — and if they screenshot it, ops
 * are told the same wrong thing. The copy now says what we actually know.
 *
 * It escalates AUTOMATICALLY, like its two siblings, and that is a deliberate reversal of an
 * earlier decision. The argument against was that this screen has a real self-serve fix, so
 * alerting on everyone would page ops about a crowd who are one tap from solving it themselves.
 * The argument that wins:
 *
 *   • Retrying **costs the tenant a bank check**, and they only get a few. If our detection is at
 *     fault, silence marches them through their budget and into the attempt-limit dead end — where
 *     we alert anyway, later, with the tenant more frustrated and their retries gone.
 *   • We cannot tell from here whether the tenant picked the wrong account or our analysis missed a
 *     real salary. When we cannot tell, the tenant should not be the one carrying the risk.
 *   • The volume worry is self-correcting: the Slack alert is deduped to one per tenant per topic
 *     per day, and the stuck-journey scan DROPS anyone whose banking later succeeds, so a tenant
 *     who fixes it themselves clears off the ops worklist without anyone touching it.
 *
 * "Try again" stays, and renders immediately rather than waiting on the escalation — where the
 * tenant has a real fix of their own, they should never wait on us to finish telling ops.
 *
 * These tenants already reach the cpbase Stuck Journeys page under the existing `banking` stage:
 * a non-success attempt is exactly what that cohort is built from. No new cohort — a second one
 * would only split the same population in two.
 */
export default function BankingSorryScreen({ theme }) {
  const navigate = useNavigate();
  const userIdentifier = localStorage.getItem("user_identifier");
  const partner = localStorage.getItem("partnerIdentifier");

  return (
    <SupportEscalationScreen
      theme={theme}
      topic="banking-salary-not-found"
      reason="Bank check did not succeed on the tenant's latest attempt"
      problemLine="My bank check did not work and I could not continue."
      action={
        // PrimaryButton reads the partner colour from ThemeContext itself — it takes no className.
        <PrimaryButton
          text="Try again"
          handleClick={() =>
            navigate(`/b/${userIdentifier}?partner=${partner}&step=banking`)
          }
        />
      }
      copy={{
        idle: {
          title: "Please wait",
          body: "We are checking your details.",
        },
        sent: {
          title: "We are looking into it",
          body: "Your bank check did not work. Our team is checking why. You can also try again and choose the bank account where you get your salary.",
        },
        failed: {
          title: "We are here to help",
          body: "Your bank check did not work. Please try again and choose the bank account where you get your salary.",
        },
      }}
    />
  );
}
